import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Message, MessageService } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { FileRemoveEvent, FileSelectEvent, FileUpload, FileUploadErrorEvent, FileUploadHandlerEvent } from 'primeng/fileupload';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';
import { AuthRoles } from 'src/app/auth/auth.types';
import { EventStepperDataService } from 'src/app/events/event-form-stepper/event-stepper-data.service';
import { LookupService } from 'src/app/services/lookup.service';
import { PeopleService } from 'src/app/services/people.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { TrimValueAccessorDirective } from 'src/app/shared/directives/trim-value-accessor.directive';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { environment } from 'src/environments/environment';
@Component({
  selector: 'ness-person-form',
  templateUrl: './person-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TrimValueAccessorDirective]
})
export class PersonFormComponent implements OnInit, OnDestroy {
  @Input() mode: string = null;
  @Input() isPersonStaging: boolean = false;
  @Input() addPeopleAddress: boolean = false;
  @Input() isEventPerson: boolean = false;
  @Input() isEventAddPersonSearch: boolean = false;
  @Output() closeForm = new EventEmitter<string>();
  @Output() onPersonSaved = new EventEmitter<ServerModels.ENESS_PersonDTO>();
  @ViewChild('fileInput', { static: false }) fileInput: FileUpload;

  readonly maxFileSize: number = environment.maxUploadFileSizeInBytes;
  readonly _maxFileLimitExceededMsg = "Total number of files exceed the max limit. No files were uploaded. ";
  readonly rows: number = AppConstants.defaultTableRows;
  readonly totalNumberOfUploadFilesAllowed: number = AppConstants.totalNumberOfUploadFilesAllowed;

  addEditLocationTitleText: string = null;
  allowedFileTypes: string = '.pdf,.PDF,.jpg,.JPG,.png,.PNG,.tiff,.TIFF,.jpeg,.JPEG,.anb,.ANB,.doc,.DOC,.docx,.DOCX,.xls,.XLS,.xlsx,.XLSX,.ppt,.PPT,.pptx,.PPTX,.msg,.MSG';
  apiRoot: string = environment.apiEndpoint;
  associationButtonLabel: string = "Associate with person";
  attachmentUploadErrorMessage: string;
  canEditPerson: AuthRoles[] = AppPermissions.canEditPerson;
  cities: ServerModels.ENESS_REF_Cities = null;
  displayAttachmentErrorMessage: boolean = false;
  displaySidebar: boolean = false;
  existingLocationAssociationButtonLabel: string = "Associate selected address to person";
  isEventAddEditPersonForm: boolean = false;
  isStandaloneForm: boolean = false;
  locationFormMode: string = null;
  minDateRule: Date = new Date('1900-01-01T00:00:00.000Z');
  maxDateRule: Date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  openFindExistingLocationForm: boolean = false;
  openLocationViewDetail: boolean = false;
  openNewLocationForm: boolean = false;
  peopleAddressesFormGroup: FormGroup;
  peopleAddresses: ServerModels.ENESS_XREF_PeopleAddressesDTO[] = [];
  personAttachmentsForm: FormGroup;
  personAttachments: ServerModels.ENESS_XREF_PeopleAttachmentDTO[] = [];
  personCityIds: ServerModels.ENESS_XREF_PeopleCityIDNumbersDTO[] = [];
  personCityIdsForm: FormGroup;
  personDriversLicenseNumberForm: FormGroup;
  personDriversLicenseNumbers: ServerModels.ENESS_XREF_PeopleDriversLicenseDTO[] = [];
  personEmailAccounts: ServerModels.ENESS_XREF_PeopleEmailAccountsDTO[] = [];
  personForm: FormGroup;
  personFbiNumbers: ServerModels.ENESS_XREF_PeopleFBIFileNumbersDTO[] = [];
  personFBINumbersForm: FormGroup;
  personSocialMediaIds: ServerModels.ENESS_XREF_PeopleSocialMediaDTO[] = [];
  personSocialMediasForm: FormGroup;
  personStateIds: ServerModels.ENESS_XREF_PeopleStateIDNumbersDTO[] = [];
  personStateIdsForm: FormGroup;
  phoneNumberFormGroup: FormGroup;
  searchAddress$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  textAreaRows: number = AppConstants.defaultTextAreaRows;
  filteredCities$: BehaviorSubject<ServerModels.ENESS_REF_Cities[]> = new BehaviorSubject([]);
  showConfirmEdit: boolean = false;

  private _citiesByState: ServerModels.ENESS_REF_Cities[] = [];
  private citiesByState: BehaviorSubject<ServerModels.ENESS_REF_Cities[]> = new BehaviorSubject<ServerModels.ENESS_REF_Cities[]>(this._citiesByState);
  public citiesByState$: Observable<ServerModels.ENESS_REF_Cities[]> = this.citiesByState.asObservable();

  private _titleText: string = 'Add a new person';
  @Input() set titleText(title: string) {
    this._titleText = title;
  }
  get titleText(): string {
    return this._titleText;
  }

  private _eventAddEditPerson: ServerModels.ENESS_PersonDTO = this.personService.createEmptyPersonDTO();
  @Input() set eventAddEditPerson(person: ServerModels.ENESS_PersonDTO) {
    this.isEventAddEditPersonForm = this.isEventPerson;
    this._eventAddEditPerson = person;
  }

  private destroyed$: Subject<void> = new Subject<void>();

  public get ENESS_XREF_PeopleAddresses(): FormArray {
    return this.personForm.get('ENESS_XREF_PeopleAddresses') as FormArray;
  }

  public get ENESS_XREF_PeoplePhoneNumbers(): FormArray {
    return this.personForm.get('ENESS_XREF_PeoplePhoneNumbers') as FormArray;
  }

  constructor(public lookupService: LookupService,
    public eventDataSvc: EventStepperDataService,
    public messageService: MessageService,
    public personService: PeopleService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public elementRef: ElementRef) {

    this.isStandaloneForm = this.route.component['name'] === 'PersonFormComponent';
  }

  ngOnInit() {

    this.personFBINumbersForm = this.fb.group({
      FBIFileNumber: ['', Validators.required]
    });

    this.personCityIdsForm = this.fb.group({
      StateID: ['', Validators.required],
      CityID: [{ value: '', disabled: true }, Validators.required],
      CityIDNumber: ['', Validators.required]
    });

    this.personStateIdsForm = this.fb.group({
      StateID: ['', Validators.required],
      StateIDNumbers: ['', Validators.required]
    });

    this.personSocialMediasForm = this.fb.group({
      SocialMediaTypeID: ['', Validators.required],
      SocialMediaHandle: ['', Validators.required]
    });

    this.personDriversLicenseNumberForm = this.fb.group({
      StateID: ['', Validators.required],
      DriversLicenseNumber: ['', Validators.required]
    });

    this.phoneNumberFormGroup = this.fb.group({
      PhoneNumber: ['', Validators.required],
      PhoneNumberTypeID: ['', Validators.required],
      PhoneNumberOtherType: [{ value: null, disabled: true }, [NESSValidators.conditionalRequired(() => this.phoneNumberFormGroup.get('PhoneNumberTypeID').value.toLowerCase() == this.lookupPhoneNumberType('Other')), Validators.maxLength(80)]],
      IsPrimary: false
    });

    this.peopleAddressesFormGroup = this.fb.group({
      AddressTypeID: ['', Validators.required],
      OtherAddressType: [{ value: null, disabled: true }, [NESSValidators.conditionalRequired(() => this.peopleAddressesFormGroup.get('AddressTypeID').value.toLowerCase() == this.lookupAddressType('Other')), Validators.maxLength(80)]],
      IsCurrent: false
    });

    this.personAttachmentsForm = this.fb.group({
      PersonAttachmentID: '',
      PersonID: '',
      AttachmentID: '',
      ENESS_PersonAttachments: this.fb.group({
        FileName: '',
        PersonAttachmentID: '',
        CreatedBy: '',
        CreatedById: ''
      })
    });

    this.personForm = this.fb.group({
      PersonID: null,
      PersonStagingID: null,
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      MiddleName: '',
      Suffix: '',
      Alias: '',
      NickName: '',
      GangName: '',
      DateOfBirth: '',
      SSN: '',
      IsKnownFelon: false,
      EmailAddress: ['', Validators.maxLength(128)],
      PersonDescription: ['', Validators.maxLength(600)],
      GenderID: '',
      GenderName: '',
      RaceID: '',
      RaceName: '',
      ENESS_XREF_PeopleCityIDNumbers: [...this.personCityIds],
      ENESS_XREF_PeopleStateIDNumbers: [...this.personStateIds],
      ENESS_XREF_PeopleFBIFileNumbers: [...this.personFbiNumbers],
      ENESS_XREF_PeopleSocialMedia: [...this.personSocialMediaIds],
      ENESS_XREF_PeopleDriversLicense: [...this.personDriversLicenseNumbers],
      ENESS_XREF_PeoplePhoneNumbers: this.fb.array([], NESSValidators.onlyOne({ value: 'IsPrimary' })),
      ENESS_XREF_PeopleAddresses: this.fb.array([], NESSValidators.onlyOne({ value: 'IsCurrent' })),
      ENESS_XREF_PeopleAttachments: [...this.personAttachments]
    });

    this.phoneNumberFormGroup.get('PhoneNumberTypeID').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: string) => {
      if (value.toLowerCase() == this.lookupPhoneNumberType('Other')) {
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').enable();
      } else {
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').setValue(null);
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').disable();
      }
    });

    if (this.route.snapshot.routeConfig.component.name === 'PersonFormComponent') {
      this.route.paramMap.subscribe((params: ParamMap) => {
        let personId = params.get('id') || null;
        if (!!personId) {
          this._getPerson(personId);
          this._titleText = 'Edit person'
        }
      });
    } else {

      this._populateFormControls();
    }
  }

  ngOnDestroy(): void {
    this.mode = null;
    this._titleText = null;
    this.isEventAddEditPersonForm = false;
    this._eventAddEditPerson = null;
    this.messageService.clear('personFormMsg');
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.fileInput.uploadedFileCount = this.personAttachments.length;
    if (this.addPeopleAddress) {
      this.ScrollToPeopleAddressTable();
    }
  }

  public closePersonForm() {
    this._resetPersonForm();

    if (this.isEventAddEditPersonForm) {
      this.closeForm.emit('');
    } else if (this.route.snapshot.routeConfig.component.name === 'PersonFormComponent') {
      this.router.navigate(['/people']);
    } else {
      this.closeForm.emit('');
    }
  }

  public dateOnBlur(calendar: Calendar): void {
    let inputValue: string = calendar.el.nativeElement.querySelector(`#${calendar.inputId}`).value;
    try {
      if (!!inputValue && calendar.parseValueFromString(inputValue.trim())) {
        if (new Date(inputValue) < this.minDateRule) throw new RangeError('Date cannot be before 1900');
        calendar.writeValue(inputValue.trim());
        this.personForm.get('DateOfBirth').setValue(inputValue.trim());
        this.personForm.get('DateOfBirth').updateValueAndValidity();
      }
    } catch (e) {
      this.personForm.get('DateOfBirth').setErrors({ invalidDate: true });
    }
  }

  public filterCities(event: any) {
    let cities = this._citiesByState.filter(x => ~x.CityName.toLowerCase().indexOf(event.query.toLowerCase()));
    this.filteredCities$.next(cities);
  }

  public onSelectCityState(stateId?: string): void {
    if (!stateId)
      return;

    this.personCityIdsForm.get('CityID').reset({ value: '', disabled: !stateId });
    this.lookupService.getCitiesByState(stateId).pipe(take(1)).subscribe((cities: ServerModels.ENESS_REF_Cities[]) => {
      this._citiesByState = cities;
      this.citiesByState.next(this._citiesByState);
    });
  }

  public lookupPhoneNumberType(typeName: string): string | null {
    const typeObj: ServerModels.ENESS_REF_PhoneNumberTypes = this.lookupService.phoneNumberTypesSubj.getValue().find((x: ServerModels.ENESS_REF_PhoneNumberTypes) => x.PhoneNumberType == typeName);

    if (typeObj && Object.keys(typeObj).length) {
      return typeObj.PhoneNumberTypeID.toLowerCase();
    }

    return null;
  }

  public onSelectPrimaryPhoneNumber(rowIdx: number): void {
    this.ENESS_XREF_PeoplePhoneNumbers.controls.forEach((x: AbstractControl, i: number) => {
      x.get('IsPrimary').setValue(i == rowIdx);
    });
  }

  public savePerson(): void {
    this._updatePersonEmailAccounts();
    let newPerson = <ServerModels.ENESS_PersonDTO>Object.assign({}, this.personForm.value);
    newPerson.CreatedBy = this._eventAddEditPerson.CreatedBy;
    newPerson.CreatedOn = this._eventAddEditPerson.CreatedOn;
    newPerson.ENESS_XREF_PeopleEmailAccounts = [...this.personEmailAccounts];
    newPerson.ENESS_XREF_PeopleCityIDNumbers = [...this.personCityIds];
    newPerson.ENESS_XREF_PeopleStateIDNumbers = [...this.personStateIds];
    newPerson.ENESS_XREF_PeopleFBIFileNumbers = [...this.personFbiNumbers];
    newPerson.ENESS_XREF_PeopleSocialMedia = [...this.personSocialMediaIds];
    newPerson.ENESS_XREF_PeopleDriversLicense = [...this.personDriversLicenseNumbers];
    newPerson.ENESS_XREF_PeopleAddresses.forEach(x => delete x.ENESS_Address.ENESS_REF_State);

    if (this.isEventAddEditPersonForm) {
      let savePersonSubscription = this.isPersonStaging ?
        this.personService.addOrUpdatePersonStagingAsync(newPerson) : this.personService.addOrUpdatePerson(newPerson);
      savePersonSubscription.pipe(takeUntil(this.destroyed$)).subscribe({
        next: (personResult: ServerModels.ENESS_PersonDTO) => {
          this._resetPersonForm();
          this.onPersonSaved.emit(personResult);
        },
        error: (err: any) => this.messageService.add({ key: 'personFormMsg', severity: 'error', detail: 'Error:', summary: err.error || err.error.ExceptionMessage })
      });
    } else {
      // only used when creating new person directly from people page
      if (!newPerson.PersonID) {
        newPerson.ENESS_XREF_PeopleAttachments = [...this.personAttachments];
      }
      this.personService.addOrUpdatePerson(newPerson).pipe(takeUntil(this.destroyed$)).subscribe({
        next: (personResult: ServerModels.ENESS_PersonDTO) => {
          this._resetPersonForm();
          if (this.route.snapshot.routeConfig.component.name === 'PersonFormComponent') {
            this.router.navigate(['/people']);
          } else {
            this.onPersonSaved.emit(personResult);
          }
        },
        error: (err: any) => this.messageService.add({ key: 'personFormMsg', severity: 'error', detail: 'Error:', summary: err.error || err.error.ExceptionMessage })
      });
    }
  }

  public deleteUploadedAttachment(idx: number) {
    this.fileInput.msgs = [];
    if (this.isEventAddEditPersonForm) {
      let personAttachmentId: string = this.personAttachments[idx].PersonAttachmentStagingID;
      if (!!this.personAttachments[idx].AttachmentStagingID) {
        this.personService.deletePersonAttachmentStaging(personAttachmentId).pipe(takeUntil(this.destroyed$)).subscribe({
          next: (result: number) => {
            if (result == -1) {
              this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: 'An error occurred deleting person attachment.' });
            } else {
              this.personAttachments = [...this.personAttachments.filter((val, i) => i != idx)];
              this.personForm.get('ENESS_XREF_PeopleAttachments').setValue(this.personAttachments);
              this.fileInput.uploadedFileCount = this.personAttachments.length;
              if (this.fileInput.hasFiles() && !this.fileInput.isFileLimitExceeded()) {
                this.fileInput.upload();
              }
              this.fileInput.cd.markForCheck();
            }
          },
          error: (err: any) => this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message })
        });
      }
      else {
        this.removeUploadedAttachmentNotStaging(idx);
      }
    }
    else {
      this.removeUploadedAttachmentNotStaging(idx);
    }
  }

  public removeUploadedAttachmentNotStaging(idx: number): void {
    let personAttachmentID: string = this.personAttachments[idx].AttachmentID;
    this.personService.deletePersonAttachment(personAttachmentID).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (result: number) => {
        if (result == -1) {
          this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: 'An error occurred deleting person attachment.' });
        } else {
          this.personAttachments = [...this.personAttachments.filter((val, i) => i != idx)];
          this.personForm.get('ENESS_XREF_PeopleAttachments').setValue(this.personAttachments);
          this.fileInput.uploadedFileCount = this.personAttachments.length;
          if (this.fileInput.hasFiles() && !this.fileInput.isFileLimitExceeded()) {
            this.fileInput.upload();
          }
          this.fileInput.cd.markForCheck();
        }
      },
      error: (err: any) => this.fileInput.msgs.push({ severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message })
    });
  }

  public onRemoveAttachment(removeEvt: FileRemoveEvent): void {
    this.fileInput.uploadedFileCount = this.personAttachments.length;
    if (this.fileInput.hasFiles() && ((this.fileInput.uploadedFileCount + this.fileInput.files.length - 1) <= this.fileInput.fileLimit)) {
      let index = this.fileInput.files.findIndex(x => x == removeEvt.file);
      this.fileInput.clearInputElement();
      this.fileInput.files.splice(index, 1);
      let errorMsg = <HTMLInputElement>document.getElementsByClassName('p-message-close-icon')[0];
      if (errorMsg != null) { errorMsg.click(); }
      this.fileInput.upload();
    }
  }

  private _updateFileInputUploadedFilesCount(): void {
    this.fileInput.uploadedFileCount = this.personAttachments?.length ?? 0;
    this.fileInput.cd.markForCheck();
  }

  public onSelectAttachment(evt: FileSelectEvent): void {
    this._updateFileInputUploadedFilesCount();
    this.fileInput.checkFileLimit(evt.files);
    if (this.fileInput.hasFiles() && !this.fileInput.isFileLimitExceeded()) {
      this.fileInput.upload();
    }
    this.fileInput.files = [];
    this.fileInput.clearInputElement();
    this.fileInput.cd.markForCheck();
  }

  public uploadPersonAttachments(uploadEvent: FileUploadHandlerEvent): void {
    if (this.fileInput.fileLimit && this.fileInput.fileLimit >= this.fileInput.files.length + (this.personAttachments?.length ?? 0)) {
      let personFormData = new FormData();
      personFormData.append('PersonID', this._eventAddEditPerson.PersonID);
      personFormData.append('PersonStagingID', this._eventAddEditPerson.PersonStagingID);
      uploadEvent.files.forEach((file) => personFormData.append('files[]', file));
      if (this.isEventAddEditPersonForm && !this.isEventAddPersonSearch) {
        this.personService.uploadPersonAttachmentsStaging(personFormData).pipe(takeUntil(this.destroyed$)).subscribe({
          next: (results: ServerModels.ENESS_XREF_PeopleAttachmentDTO[]) => {
            this.personAttachments = [...this.personAttachments, ...results];
            this.personForm.get('ENESS_XREF_PeopleAttachments').setValue(this.personAttachments);
            this._uploadComplete();
            this.fileInput.cd.markForCheck();
            this.cdr.detectChanges();
          },
          error: (err: any) => this.fileInput.onError.emit({ files: this.fileInput.files, error: err })
        });
      }
      else {
        this.personService.uploadPersonAttachments(personFormData).pipe(takeUntil(this.destroyed$)).subscribe({
          next: (results: ServerModels.ENESS_XREF_PeopleAttachmentDTO[]) => {
            this.personAttachments = [...this.personAttachments, ...results];
            this.personForm.get('ENESS_XREF_PeopleAttachments').setValue(this.personAttachments);
            this._uploadComplete();
            this.fileInput.cd.markForCheck();
            this.cdr.detectChanges();
          },
          error: (err: any) => this.fileInput.onError.emit({ files: this.fileInput.files, error: err })
        });
      }

    }
  }

  public onUploadErrorHandler(errorEvent: FileUploadErrorEvent) {
    let messages: Message[] = [];
    if (this.fileInput.msgs) {
      this.fileInput.msgs.forEach(x => {
        if (x.summary.toLowerCase().indexOf('invalid file type') > -1) {
          messages.push(x);
        }
      });
    }
    this._uploadComplete();
    this.fileInput.msgs = [...messages];
    this.fileInput.msgs.push({ severity: 'error', summary: 'Attachmend Upload Error:', detail: errorEvent.error.error });
  }

  public insertFBINumber() {
    if (!this.personFBINumbersForm.valid)
      return;

    this.personFbiNumbers = [
      ...[{
        PeopleFBIFileNumberStagingID: null,
        PeopleFBIFileNumbersID: null,
        PersonID: this._eventAddEditPerson.PersonID,
        PersonStagingID: this._eventAddEditPerson.PersonStagingID,
        FBIFileNumber: this.personFBINumbersForm.get('FBIFileNumber').value
      } as ServerModels.ENESS_XREF_PeopleFBIFileNumbersDTO],
      ...this.personFbiNumbers
    ];

    this.personForm.get('ENESS_XREF_PeopleFBIFileNumbers').setValue(this.personFbiNumbers);
    this.personFBINumbersForm.reset();
  }

  public removeFbiNumber(index: number): void {
    this.personFbiNumbers.splice(index, 1);
    this.personForm.get('ENESS_XREF_PeopleFBIFileNumbers').setValue([...this.personFbiNumbers]);
  }

  public insertStateId(stateName?: string) {
    if (!this.personStateIdsForm.valid)
      return;

    this.personStateIds = [
      ...[{
        PeopleStateIDNumbersStagingID: null,
        PeopleStateIDNumbersID: null,
        PersonID: this._eventAddEditPerson.PersonID,
        PersonStagingID: this._eventAddEditPerson.PersonStagingID,
        StateID: this.personStateIdsForm.get('StateID').value,
        StateName: stateName,
        StateIDNumbers: this.personStateIdsForm.get('StateIDNumbers').value
      } as ServerModels.ENESS_XREF_PeopleStateIDNumbersDTO],
      ...this.personStateIds
    ];

    this.personForm.get('ENESS_XREF_PeopleStateIDNumbers').setValue(this.personStateIds);
    this.personStateIdsForm.reset({
      StateID: ''
    });
  }

  public removeStateId(index: number): void {
    this.personStateIds.splice(index, 1);
    this.personForm.get('ENESS_XREF_PeopleStateIDNumbers').setValue([...this.personStateIds]);
  }

  public insertCityId(stateName: string) {
    if (!this.personCityIdsForm.valid)
      return;

    this.personCityIds = [
      ...[{
        PeopleCityIDNumberStagingID: null,
        PeopleCityIDNumbersID: null,
        PersonID: this._eventAddEditPerson.PersonID,
        PersonStagingID: this._eventAddEditPerson.PersonStagingID,
        CityID: this.cities.CityID,
        CityName: this.cities.CityName,
        StateID: this.cities.StateID,
        StateName: stateName,
        CityIDNumber: this.personCityIdsForm.get('CityIDNumber').value
      } as ServerModels.ENESS_XREF_PeopleCityIDNumbersDTO],
      ...this.personCityIds
    ];

    this.personForm.get('ENESS_XREF_PeopleCityIDNumbers').setValue(this.personCityIds);
    this.personCityIdsForm.reset({
      CityID: '',
      StateID: ''
    });
  }

  public removeCityId(index: number): void {
    this.personCityIds.splice(index, 1);
    this.personForm.get('ENESS_XREF_PeopleCityIDNumbers').setValue([...this.personCityIds]);
  }

  public insertSocialMediaId(socialMediaTypeName?: string) {
    if (!this.personSocialMediasForm.valid)
      return;

    this.personSocialMediaIds = [
      ...[{
        PeopleSocialMediaStagingID: null,
        PersonSocialMediaID: null,
        PersonID: this._eventAddEditPerson.PersonID,
        PersonStagingID: this._eventAddEditPerson.PersonStagingID,
        SocialMediaTypeID: this.personSocialMediasForm.get('SocialMediaTypeID').value,
        SocialMediaTypeName: socialMediaTypeName,
        SocialMediaHandle: this.personSocialMediasForm.get('SocialMediaHandle').value
      } as ServerModels.ENESS_XREF_PeopleSocialMediaDTO],
      ...this.personSocialMediaIds
    ];

    this.personForm.get('ENESS_XREF_PeopleSocialMedia').setValue(this.personSocialMediaIds);
    this.personSocialMediasForm.reset({
      SocialMediaTypeID: ''
    });
  }

  public removeSocialMedia(index: number): void {
    this.personSocialMediaIds.splice(index, 1);
    this.personForm.get('ENESS_XREF_PeopleSocialMedia').setValue([...this.personSocialMediaIds]);
  }

  public insertDriversLicense(stateName?: string) {
    if (!this.personDriversLicenseNumberForm.valid)
      return;

    this.personDriversLicenseNumbers = [
      ...[{
        PeopleDriversLicenseStagingID: null,
        PeopleDriversLicenseID: null,
        PersonID: this._eventAddEditPerson.PersonID,
        PersonStagingID: this._eventAddEditPerson.PersonStagingID,
        DriversLicenseNumber: this.personDriversLicenseNumberForm.get('DriversLicenseNumber').value,
        StateID: this.personDriversLicenseNumberForm.get('StateID').value,
        StateName: stateName
      } as ServerModels.ENESS_XREF_PeopleDriversLicenseDTO],
      ...this.personDriversLicenseNumbers
    ];

    this.personForm.get('ENESS_XREF_PeopleDriversLicense').setValue(this.personDriversLicenseNumbers);
    this.personDriversLicenseNumberForm.reset({
      StateID: ''
    });
  }

  public removeDriversLicense(index: number): void {
    this.personDriversLicenseNumbers.splice(index, 1);
    this.personForm.get('ENESS_XREF_PeopleDriversLicense').setValue([...this.personDriversLicenseNumbers]);
  }

  public insertPhoneNumber(phoneType?: string): void {
    if (!this.phoneNumberFormGroup.valid)
      return;

    if (this.phoneNumberFormGroup.get('IsPrimary').value == true) {
      (<FormArray>this.personForm.get('ENESS_XREF_PeoplePhoneNumbers')).controls.forEach((ctrl: FormGroup) => ctrl.get('IsPrimary').setValue(false));
    }

    let phoneNumber = this.fb.group({
      PeoplePhoneNumberStagingID: null,
      PeoplePhoneNumbersID: null,
      PersonStagingID: this.personForm.get('PersonStagingID').value,
      PersonID: this.personForm.get('PersonID').value,
      PhoneNumber: [this.phoneNumberFormGroup.get('PhoneNumber').value, Validators.required],
      PhoneNumberTypeID: [this.phoneNumberFormGroup.get('PhoneNumberTypeID').value, Validators.required],
      PhoneNumberType: phoneType,
      PhoneNumberOtherType: [{ value: this.phoneNumberFormGroup.get('PhoneNumberOtherType').value, disabled: this.phoneNumberFormGroup.get('PhoneNumberOtherType').disabled }, NESSValidators.conditionalRequired(() => this.phoneNumberFormGroup.get('PhoneNumberTypeID').value.toLowerCase() == this.lookupPhoneNumberType('Other'))],
      IsPrimary: this.phoneNumberFormGroup.get('IsPrimary').value || false,
      StartDate: null,
      EndDate: null,
      CreatedOn: null,
      Deleted: false
    });

    if (phoneNumber.get('IsPrimary').value == true) {
      (<FormArray>this.personForm.get('ENESS_XREF_PeoplePhoneNumbers')).insert(0, phoneNumber);
    } else {
      (<FormArray>this.personForm.get('ENESS_XREF_PeoplePhoneNumbers')).push(phoneNumber);
    }
    this.phoneNumberFormGroup.reset({
      PhoneNumberTypeID: '',
      IsPrimary: false
    });
  }

  public removePhoneNumber(idx: number): void {
    this.ENESS_XREF_PeoplePhoneNumbers.removeAt(idx);
    this.ENESS_XREF_PeoplePhoneNumbers.updateValueAndValidity();
  }

  public onAddNewLocationClick(): void {
    this.locationFormMode = 'add';
    this.addEditLocationTitleText = 'Add a new address';
    if (this.isEventPerson) {
      this._updatePersonEmailAccounts();
      let _personDetails = <ServerModels.ENESS_PersonDTO>Object.assign({}, this.personForm.value);
      _personDetails.ENESS_XREF_PeopleEmailAccounts = [...this.personEmailAccounts];
      this.eventDataSvc.setPersonDetails(_personDetails);
      this.closeForm.emit("FindNewAddress");
    }
    else {
      this.displaySidebar = true;
      this.openFindExistingLocationForm = false;
      this.openNewLocationForm = true;
    }
  }

  public onOpenFindExistingLocationClick() {
    if (this.isEventPerson) {
      this._updatePersonEmailAccounts();
      let _personDetails = <ServerModels.ENESS_PersonDTO>Object.assign({}, this.personForm.value);
      _personDetails.ENESS_XREF_PeopleEmailAccounts = [...this.personEmailAccounts];
      this.eventDataSvc.setPersonDetails(_personDetails);
      this.closeForm.emit("FindExistingAddress");
    }
    else {
      this.displaySidebar = true;
      this.openFindExistingLocationForm = true;
    }
  }

  public onNewLocationAdded(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.messageService.clear();
    if (!newAddress) {
      return;
    }

    delete newAddress.ENESS_REF_State;
    let _address = this.fb.group({
      PeopleAddressStagingID: null,
      PeopleAddressesID: null,
      AddressTypeID: ['', Validators.required],
      OtherAddressType: [{ value: null, disabled: true }, [NESSValidators.conditionalRequired(() => _address.get('AddressTypeID').value == this.lookupAddressType('Other')), Validators.maxLength(80)]],
      ENESS_Address: newAddress,
      AddressID: null,
      IsCurrent: false,
      PersonID: this._eventAddEditPerson?.PersonID,
      PersonStagingID: this._eventAddEditPerson?.PersonStagingID
    });
    (<FormArray>this.personForm.get('ENESS_XREF_PeopleAddresses')).push(_address);
    this.personForm.updateValueAndValidity();

    this.onNewLocationClose();
    this.ScrollToPeopleAddressTable()
  }

  public onNewLocationClose(): void {
    this.openNewLocationForm = false;
    this.displaySidebar = false;
  }

  public onAddExistingLocations(newAddress: ServerModels.ENESS_AddressDTO[]): void {
    this.messageService.clear();
    if (this.isEventPerson) {
      let _personDetails = this.personService.personDetails.getValue();
      this._eventAddEditPerson = { ..._personDetails };
      this._populateFormControls();
    }

    if (newAddress != null && !!Object.keys(newAddress).length) {
      newAddress.forEach((loc) => {
        let _address = this.fb.group({
          AddressTypeID: ['', Validators.required],
          OtherAddressType: [{ value: null, disabled: true }, [NESSValidators.conditionalRequired(() => _address.get('AddressTypeID').value == this.lookupAddressType('Other')), Validators.maxLength(80)]],
          ENESS_Address: loc,
          AddressID: loc.AddressID,
          IsCurrent: false,
          PersonID: (this._eventAddEditPerson && this._eventAddEditPerson.PersonID ? this._eventAddEditPerson.PersonID : null),
          PeopleAddressesID: null
        });
        (<FormArray>this.personForm.get('ENESS_XREF_PeopleAddresses')).push(_address);
      });
      this.personForm.updateValueAndValidity();
    }
    this.onNewLocationClose();
    this.ScrollToPeopleAddressTable()
  }

  public onAddExistingLocationsClose(): void {
    this.openFindExistingLocationForm = false;
    this.displaySidebar = false;
  }

  public removeAddress(index: number): void {
    this.ENESS_XREF_PeopleAddresses.removeAt(index);
    this.ENESS_XREF_PeopleAddresses.updateValueAndValidity();
  }

  public onAddressTypeChange(selectedAddressType: string, index: number) {
    let fGroup = this.ENESS_XREF_PeopleAddresses.at(index);

    if (selectedAddressType.toLowerCase() == this.lookupAddressType('Other')) {
      fGroup.get('OtherAddressType').enable();
      fGroup.get('OtherAddressType').setValidators([NESSValidators.conditionalRequired(() => fGroup.get('AddressTypeID').value == this.lookupAddressType('Other')), Validators.maxLength(80)])
    }
    else {
      fGroup.get('OtherAddressType').setValue(null);
      fGroup.get('OtherAddressType').disable();
    }

    fGroup.get('OtherAddressType').updateValueAndValidity();
  }

  public onPrimaryAddressChange(e: Event, index: number) {

    let fGroup = this.ENESS_XREF_PeopleAddresses.at(index);
    if ((<HTMLInputElement>e.target).checked) {
      for (let control of this.ENESS_XREF_PeopleAddresses.controls) {
        control.get('IsCurrent').setValue(false);
      }
      fGroup.get('IsCurrent').setValue(true);
    }
    this.ENESS_XREF_PeopleAddresses.updateValueAndValidity();
  }

  public onShowSidebar(): void {
    let targetEl = this.elementRef.nativeElement.getElementsByClassName('ui-sidebar-active');
    if (targetEl.length) {
      targetEl[0].scrollTop = 0;
      targetEl[0].setAttribute("tabindex", "-1");
      targetEl[0].focus();
    }
  }

  public onSidebarClose(): void {
    this.openFindExistingLocationForm = false;
    this.openNewLocationForm = false;
    this.displaySidebar = false;
  }

  public lookupAddressType(typeName: string): string | null {
    const typeObj: ServerModels.ENESS_REF_AddressTypes = this.personService.addressTypesSubj.getValue().find((x: ServerModels.ENESS_REF_AddressTypes) => x.AddressTypeName == typeName);

    if (typeObj && Object.keys(typeObj).length) {
      return typeObj.AddressTypeID;
    }

    return null;
  }

  private _updatePersonEmailAccounts(): void {
    this.personEmailAccounts = !!this.personForm.get('EmailAddress').value ? [<ServerModels.ENESS_XREF_PeopleEmailAccountsDTO>{
      CreatedOn: null,
      EmailAccount: this.personForm.get('EmailAddress').value,
      EmailAccountStatus: 'Active',
      PeopleEmailAccountsID: null,
      PeopleEmailAccountStagingID: null,
      PersonID: this._eventAddEditPerson.PersonID,
      PersonStagingID: this._eventAddEditPerson.PersonStagingID,
    }] : [];
  }

  private _resetPersonForm() {
    this.personFbiNumbers = [];
    this.personStateIds = [];
    this.personCityIds = [];
    this.personSocialMediaIds = [];
    this.personDriversLicenseNumbers = [];
    this.personAttachments = [];

    this.personForm.reset({
      GenderID: '',
      RaceID: '',
      ENESS_XREF_PeopleAddresses: this.peopleAddresses,
      ENESS_XREF_PeopleCityIDNumbers: this.personCityIds,
      ENESS_XREF_PeopleStateIDNumbers: this.personStateIds,
      ENESS_XREF_PeopleFBIFileNumbers: this.personFbiNumbers,
      ENESS_XREF_PeopleSocialMedia: this.personSocialMediaIds,
      ENESS_XREF_PeopleDriversLicense: this.personDriversLicenseNumbers,
      ENESS_XREF_PeopleAttachments: this.personAttachments,
      ENESS_XREF_PeoplePhoneNumbers: []
    });
  }

  private _getPerson(personId: string) {
    this.personService.getPersonDTOById(personId).pipe(take(1)).subscribe((person: ServerModels.ENESS_PersonDTO) => {
      this._eventAddEditPerson = { ...person };
      this._populateFormControls();
      this.cdr.detectChanges();
    });
  }

  private createAddressFormGroups(): void {
    this._eventAddEditPerson.ENESS_XREF_PeopleAddresses.forEach(a => {
      this.ENESS_XREF_PeopleAddresses.push(this.fb.group({
        PeopleAddressesID: a.PeopleAddressesID,
        PeopleAddressStagingID: a.PeopleAddressesStagingID,
        PersonID: a.PersonID,
        PersonStagingID: a.PersonStagingID,
        AddressStagingID: a.AddressStagingID,
        AddressID: a.AddressID,
        AddressTypeID: [(a.AddressTypeID || ''), Validators.required],
        OtherAddressType: [{ value: a.OtherAddressType, disabled: !a.OtherAddressType }, [NESSValidators.conditionalRequired(() => this.peopleAddressesFormGroup.get('AddressTypeID').value.toLowerCase() == this.lookupAddressType('Other')), Validators.maxLength(80)]],
        IsCurrent: a.IsCurrent || false,
        StartDate: a.StartDate,
        EndDate: a.EndDate,
        Notes: a.Notes,
        IsEditable: a.IsEditable,
        CreatedOn: a.CreatedOn,
        ENESS_Address: a.ENESS_Address
      }));
    });
  }

  private createPhoneNumberFormGroups(): void {
    this._eventAddEditPerson.ENESS_XREF_PeoplePhoneNumbers.forEach(p => {
      (<FormArray>this.personForm.get('ENESS_XREF_PeoplePhoneNumbers')).push(this.fb.group({
        PeoplePhoneNumberStagingID: p.PeoplePhoneNumberStagingID,
        PeoplePhoneNumbersID: p.PeoplePhoneNumbersID,
        PersonStagingID: p.PersonStagingID,
        PersonID: p.PersonID,
        PhoneNumber: [p.PhoneNumber, Validators.required],
        PhoneNumberTypeID: [p.PhoneNumberTypeID, Validators.required],
        PhoneNumberType: p.PhoneNumberType,
        PhoneNumberOtherType: p.PhoneNumberOtherType,
        StartDate: p.StartDate,
        EndDate: p.EndDate,
        IsPrimary: p.IsPrimary || false,
        CreatedOn: p.CreatedOn
      }));
    });
  }

  private _populateFormControls(): void {
    if (this._eventAddEditPerson == null || !Object.keys(this._eventAddEditPerson).length) {
      return;
    }

    if (this._eventAddEditPerson.ENESS_XREF_PeoplePhoneNumbers && !!this._eventAddEditPerson.ENESS_XREF_PeoplePhoneNumbers.length && this._eventAddEditPerson.ENESS_XREF_PeoplePhoneNumbers.filter(x => !!x.IsPrimary).length > 1) {
      this._eventAddEditPerson.ENESS_XREF_PeoplePhoneNumbers.forEach(x => x.IsPrimary = false);
    }

    this.createPhoneNumberFormGroups();
    this.createAddressFormGroups();
    this.personEmailAccounts = this._eventAddEditPerson.ENESS_XREF_PeopleEmailAccounts ? [...this._eventAddEditPerson.ENESS_XREF_PeopleEmailAccounts || []] : [];
    this.personFbiNumbers = [...this._eventAddEditPerson.ENESS_XREF_PeopleFBIFileNumbers];
    this.personStateIds = [...this._eventAddEditPerson.ENESS_XREF_PeopleStateIDNumbers];
    this.personCityIds = [...this._eventAddEditPerson.ENESS_XREF_PeopleCityIDNumbers];
    this.personSocialMediaIds = [...this._eventAddEditPerson.ENESS_XREF_PeopleSocialMedia];
    this.personDriversLicenseNumbers = [...this._eventAddEditPerson.ENESS_XREF_PeopleDriversLicense];
    this.personAttachments = [...this._eventAddEditPerson.ENESS_XREF_PeopleAttachments];

    this.personForm.patchValue({
      PersonID: (this._eventAddEditPerson && this._eventAddEditPerson.PersonID ? this._eventAddEditPerson.PersonID : null),
      PersonStagingID: (this._eventAddEditPerson && this._eventAddEditPerson.PersonStagingID ? this._eventAddEditPerson.PersonStagingID : null),
      FirstName: this._eventAddEditPerson.FirstName,
      LastName: this._eventAddEditPerson.LastName,
      MiddleName: this._eventAddEditPerson.MiddleName,
      Suffix: this._eventAddEditPerson.Suffix,
      Alias: this._eventAddEditPerson.Alias,
      NickName: this._eventAddEditPerson.NickName,
      GangName: this._eventAddEditPerson.GangName,
      DateOfBirth: new DatePipe('en-US').transform(this._eventAddEditPerson.DateOfBirth, 'MM/dd/yyyy'),
      SSN: this._eventAddEditPerson.SSN,
      IsKnownFelon: this._eventAddEditPerson.IsKnownFelon,
      PersonDescription: this._eventAddEditPerson.PersonDescription,
      GenderID: this._eventAddEditPerson.GenderID,
      RaceID: this._eventAddEditPerson.RaceID,
      EmailAddress: this.personEmailAccounts.length ? this.personEmailAccounts[0].EmailAccount : '',
      ENESS_XREF_PeopleFBIFileNumbers: [...this.personFbiNumbers],
      ENESS_XREF_PeopleStateIDNumbers: [...this.personStateIds],
      ENESS_XREF_PeopleCityIDNumbers: [...this.personCityIds],
      ENESS_XREF_PeopleSocialMedia: [...this.personSocialMediaIds],
      ENESS_XREF_PeopleDriversLicense: [...this.personDriversLicenseNumbers],
      ENESS_XREF_PeopleAttachments: [...this.personAttachments]
    });
    this.personForm.updateValueAndValidity({ onlySelf: true });
  }

  private _uploadComplete(): void {
    this._updateFileInputUploadedFilesCount();
    this.fileInput.uploading = false;
    this.fileInput.progress = 0;
    this.fileInput.clear();
  }

  public ScrollToPeopleAddressTable() {
    // Scroll to people address section
    window.setTimeout(() => {
      let targetEl = this.elementRef.nativeElement.getElementsByClassName('people-address-active');
      if (targetEl.length) {
        targetEl[0].scrollIntoView(true);
        targetEl[0].focus();
      }
    }, 1.0 * 1000);
  }

  onOpenConfirmation(): void {
    if (!this._eventAddEditPerson?.PersonID) {
      this.savePerson();
    }
    else {
      this.showConfirmEdit = true;
    }
  }

  onCloseConfirmation(): void {
    this.showConfirmEdit = false;
  }
}
